import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';
import { combineLatest, EMPTY, Observable, of, iif } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { TokenService } from '@services/token.service';
import { enumUserBetaFlag, enumUserRole } from '@data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { UtilsHelper } from '@helpers/utils-helper';
import { CompanyExternalIds, CompanyModel } from '@data-layer/models/companies';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BetaFlagsRepository, CompaniesRepository } from '@app/data-layer/repositories';

@Injectable({
  providedIn: 'root',
})
export class ApplyApplicationModeGuard implements CanActivate {
  constructor(
    private tokenService: TokenService,
    private companiesRepository: CompaniesRepository,
    private companiesService: CompaniesService,
    private applicationModeService: ApplicationModeService,
    private jwtHelperService: JwtHelperService,
    private betaFlagsRepository: BetaFlagsRepository,
    private router: Router,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    const organizationInvitationOrganizationId: number = route?.queryParams?.organizationId;
    const organizationInvitationCompanyId: number = route?.queryParams?.companyId;
    const organizationInvitationId: number = route?.queryParams?.organizationInvitationId;
    const organizationInvitationName: number = route?.queryParams?.organizationInvitationName;
    const organizationInvitationEmail: number = route?.queryParams?.organizationInvitationEmail;
    const accessToken = this.tokenService.getAccessToken();
    const companyFromCache = this.companiesService.getCompany();
    const isUserExist: boolean = !!route?.queryParams?.isUserExist
      ? Boolean(JSON.parse(route?.queryParams?.isUserExist.toLowerCase()))
      : false;
    if (!!organizationInvitationId) {
      localStorage.setItem('organizationInvitationOrganizationId', String(organizationInvitationOrganizationId));
      localStorage.setItem('organizationInvitationCompanyId', String(organizationInvitationCompanyId));
      localStorage.setItem('organizationInvitationId', String(organizationInvitationId));
      localStorage.setItem('organizationInvitationName', String(organizationInvitationName));
      localStorage.setItem('organizationInvitationEmail', String(organizationInvitationEmail));
    }

    if (!this.tokenService.isAuthenticated && !accessToken) {
      return this.betaFlagsRepository.getAppBetaFeatures().pipe(
        take(1),
        tap((flags) => {
          const isHasOnboardingRedesign = flags.map((flag: UserBetaFlagModel) => flag.tag).includes(enumUserBetaFlag.onboardingRedesign);
          let redirectSignUpPath: string[] = isHasOnboardingRedesign ? ['/auth', 'invite-sign-up'] : ['/auth', 'sign-up'];
          !isUserExist && !!organizationInvitationId ? this.router.navigate(redirectSignUpPath) : this.router.navigate(['/auth', 'login']);
        }),
        switchMap((flags) => of(false)),
      );
    } else if (!!organizationInvitationId) {
      // Redirect authenticated users with invitation to login page to show organization selection
      this.router.navigate(['/auth', 'login']);
      return of(false);
    } else {
      return this.companiesService.getUserData().pipe(
        switchMap((user) => {
          let getCompany$: Observable<CompanyModel>;
          if (user.defaultCompanyId) {
            getCompany$ = this.companiesRepository.getCompanyById(user.defaultCompanyId);
          } else {
            getCompany$ = this.companiesRepository.getCompanies().pipe(map((companies) => companies[0]));
          }

          return companyFromCache
            ? of({
                company: companyFromCache,
                user,
              })
            : getCompany$.pipe(
                map((company) => {
                  return { company, user };
                }),
              );
        }),
        switchMap(({ user, company }) =>
          this.companiesService
            .getUserBetaFlags({
              userExternalId: this.getUserExternalId(accessToken),
              companyId: company.id,
              organizationId: company.organizationId,
            })
            .pipe(
              switchMap((betaFlags: UserBetaFlagModel[]) =>
                combineLatest([
                  of(betaFlags),
                  user?.defaultCompanyId ? this.companiesService.getCompanyExternalIds(user.defaultCompanyId) : of(EMPTY),
                ]),
              ),
              map(([betaFlags, externalIds]: [UserBetaFlagModel[], CompanyExternalIds]) => {
                if (user) {
                  const roles: enumUserRole[] = UtilsHelper.isString(this.tokenService.getRoles())
                    ? [this.tokenService.getRoles()]
                    : this.tokenService.getRoles();
                  this.companiesService.setUserRoles(roles);
                  this.companiesService.setUser(user);
                  const uid = this.tokenService.getUID();
                  if (uid && user && user.uid !== uid) {
                    this.companiesService.setUserUID(uid);
                  }
                  const activeApplicationMode = this.applicationModeService.activeApplicationMode;
                  if (!activeApplicationMode || !this.applicationModeService.isActiveApplicationModeFitToRoles(roles)) {
                    if (roles.includes(enumUserRole.SUPER_ADMIN)) {
                      this.applicationModeService.activeApplicationMode = enumApplicationMode.SUPPORT;
                    } else if (roles.includes(enumUserRole.ADMIN)) {
                      this.applicationModeService.activeApplicationMode = enumApplicationMode.SUPPORT;
                    } else if (roles.includes(enumUserRole.USER)) {
                      this.applicationModeService.activeApplicationMode = enumApplicationMode.USER;
                    } else if (roles.includes(enumUserRole.SUPPORT)) {
                      this.applicationModeService.activeApplicationMode = enumApplicationMode.SUPPORT;
                    } else if (roles.includes(enumUserRole.CUSTOMS_BROKER)) {
                      this.applicationModeService.activeApplicationMode = enumApplicationMode.SUPPORT;
                    }
                  }
                  if (!!betaFlags) {
                    this.companiesService.setUserBetaFlags(betaFlags);
                  }
                }
                return !!user;
              }),
            ),
        ),
        tap(() => this.companiesService.getHints()),
      );
    }
  }

  private getUserExternalId(token: string): string {
    return this.jwtHelperService.decodeToken(token)?.user_external_id || null;
  }
}
